import { useRef, useState } from "react"

import { usePublicFundreadyReportCreate } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  FormProvider,
  useForm,
  type SubmitErrorHandler,
  type SubmitHandler,
} from "react-hook-form"

import { GateModal } from "@/components/fundready/GateModal"
import { IntroStep } from "@/components/fundready/IntroStep"
import { ReportStep } from "@/components/fundready/ReportStep"
import { StepAsk } from "@/components/fundready/steps/StepAsk"
import { StepAssets } from "@/components/fundready/steps/StepAssets"
import { StepBusiness } from "@/components/fundready/steps/StepBusiness"
import { StepDebt } from "@/components/fundready/steps/StepDebt"
import { StepOffer } from "@/components/fundready/steps/StepOffer"
import { StepShell } from "@/components/fundready/StepShell"
import {
  fundReadyIntakeDefaults,
  fundReadyIntakeErrorMap,
  fundReadyIntakeSchema,
  STEP_FIELDS,
} from "@/lib/fundready/schema"

import type { FundReadyReportResponse } from "@boundless/bl-api-client/public/models/FundReadyReportResponse"
import type { z } from "zod"

// The flow moves through four named phases; within "form" the active step is a
// 0-based index into STEPS, so there are no magic sentinel indices to track.
type Phase = "intro" | "form" | "gate" | "report"

const STEPS = [
  { title: "Your business", component: StepBusiness },
  { title: "The funding you want", component: StepAsk },
  { title: "What assets are in the business?", component: StepAssets },
  { title: "Current debt", component: StepDebt },
  { title: "Do you currently have an offer?", component: StepOffer },
]

export function FundReadyApp({ partnerSlug }: { partnerSlug?: string }) {
  const [phase, setPhase] = useState<Phase>("intro")
  const [stepIndex, setStepIndex] = useState(0)
  const [result, setResult] = useState<FundReadyReportResponse | null>(null)
  // Snapshot the ask amount that was actually submitted.
  const [submittedAskAmount, setSubmittedAskAmount] = useState(0)
  // Once an email is in, revised answers go straight to the report: asking
  // again would re-notify the advisor about a lead they already have. A ref
  // rather than state because only the mutation callback reads it.
  const gatePassed = useRef(false)
  // The generated body marks defaulted fields optional, so the schema's input
  // (what the form edits) and output (what submit produces) differ. Spell out
  // both so form.trigger works on the input shape and handleSubmit hands the
  // fully-resolved output straight to the mutation.
  const form = useForm<
    z.input<typeof fundReadyIntakeSchema>,
    unknown,
    z.output<typeof fundReadyIntakeSchema>
  >({
    resolver: zodResolver(fundReadyIntakeSchema, {
      errorMap: fundReadyIntakeErrorMap,
    }),
    defaultValues: fundReadyIntakeDefaults,
  })
  const reportMutation = usePublicFundreadyReportCreate({
    mutation: {
      onSuccess: (data, variables) => {
        setResult(data)
        setSubmittedAskAmount(variables.data.askAmount)
        setPhase(gatePassed.current ? "report" : "gate")
      },
    },
  })

  function handleStartOver() {
    form.reset(fundReadyIntakeDefaults)
    setResult(null)
    setSubmittedAskAmount(0)
    gatePassed.current = false
    setStepIndex(0)
    setPhase("intro")
  }

  function editAnswers() {
    setStepIndex(0)
    setPhase("form")
  }

  const submitReport: SubmitHandler<z.output<typeof fundReadyIntakeSchema>> = (
    data,
  ) =>
    reportMutation.mutate({
      data: { ...data, partnerSlug, leadUuid: result?.leadUuid },
    })

  // A final-submit error can land on a field the user can't see: superRefine
  // rules only run once the whole schema parses, so a bad value from an earlier
  // step surfaces only here, against a now-unmounted field. Jump to the first
  // step that owns an errored field so the message is actually shown instead of
  // leaving the form blocked with no feedback.
  const focusFirstInvalidStep: SubmitErrorHandler<
    z.input<typeof fundReadyIntakeSchema>
  > = (errors) => {
    const stepWithError = STEP_FIELDS.findIndex((fields) =>
      fields.some((field) => field in errors),
    )
    if (stepWithError >= 0) {
      setStepIndex(stepWithError)
    }
  }

  // The Continue gate validates the current step's fields against the schema, so
  // it never drifts from what the final submit requires. The last step runs the
  // full submit, which revalidates everything and yields the resolved output.
  async function handleContinue() {
    if (stepIndex < STEPS.length - 1) {
      const valid = await form.trigger(STEP_FIELDS[stepIndex])
      if (valid) {
        setStepIndex((index) => index + 1)
      }
      return
    }

    await form.handleSubmit(submitReport, focusFirstInvalidStep)()
  }

  if (phase === "intro") {
    return (
      <IntroStep
        onStart={() => {
          setStepIndex(0)
          setPhase("form")
        }}
      />
    )
  }

  if (phase === "report" && result) {
    return (
      <ReportStep
        askAmount={submittedAskAmount}
        report={result.report}
        lenderCounts={result.lenderCounts}
        partnerSlug={partnerSlug}
        onEditAnswers={editAnswers}
        onStartOver={handleStartOver}
      />
    )
  }

  const currentStep = STEPS[stepIndex]
  const StepComponent = currentStep.component

  const stepView = (
    <FormProvider {...form}>
      <StepShell
        step={stepIndex + 1}
        totalSteps={STEPS.length}
        title={currentStep.title}
        onBack={stepIndex > 0 ? () => setStepIndex((i) => i - 1) : undefined}
        backDisabled={reportMutation.isPending}
        onContinue={handleContinue}
        continueLabel={
          stepIndex === STEPS.length - 1
            ? "See my FundReady™ report"
            : "Continue"
        }
        continuePending={reportMutation.isPending}
      >
        <StepComponent />
        {reportMutation.isError && (
          <p role="alert" className="form-error text-sm">
            Something went wrong generating your report. Please try again.
          </p>
        )}
      </StepShell>
    </FormProvider>
  )

  // The gate keeps the step it came from on screen, blurred behind the modal,
  // so the prospect stays where they were rather than watching the flow drop
  // out from under them. Same tree shape either way so switching phases
  // doesn't remount the step underneath.
  return (
    <>
      <div
        className={
          phase === "gate"
            ? "pointer-events-none blur-[6px] select-none"
            : undefined
        }
      >
        {stepView}
      </div>
      {phase === "gate" && result && (
        <GateModal
          leadUuid={result.leadUuid}
          report={result.report}
          lenderCounts={result.lenderCounts}
          onSuccess={() => {
            gatePassed.current = true
            setPhase("report")
          }}
          onBack={() => setPhase("form")}
        />
      )}
    </>
  )
}
